import { useState, useMemo, useRef, useEffect } from 'react';
import { DateField, Select } from '@ui';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { useRegionAndLocationContext } from '../../contexts/RegionAndLocationContext';
import { formatStoredDateForDisplay } from '../../utils/dateInputUtils';
import '../../css/ReportFilterBar.css';

export interface ReportFilterValues {
    equipmentCategoryId: string;
    unitId: string;
    dateStart: string;
    dateEnd: string;
    locationId: string;
    districtId: string;
    regionId: string;
}

export type UnitReportScope = {
    unitId: string;
    equipmentCategoryId: string;
};

interface ReportFilterBarProps {
    onFilterChange: (filters: ReportFilterValues) => void;
    companyId: string;
    initialFilters?: Partial<ReportFilterValues>;
    /** Key used to persist filters in sessionStorage across navigation. */
    storageKey?: string;
    /** When set, unit and category filters stay locked to this equipment. */
    scopeFilters?: UnitReportScope;
}

const EMPTY_FILTERS: ReportFilterValues = {
    equipmentCategoryId: '',
    unitId: '',
    dateStart: '',
    dateEnd: '',
    locationId: '',
    districtId: '',
    regionId: '',
};

export const ReportFilterBar = ({
    onFilterChange,
    companyId: _companyId,
    initialFilters,
    storageKey,
    scopeFilters,
}: ReportFilterBarProps) => {
    const { companyEquipmentCategories, companyUnits } = useEquipmentContext();
    const { companyLocations, companyRegions, companyDistricts } = useRegionAndLocationContext();

    const mergeScope = (next: ReportFilterValues): ReportFilterValues => {
        if (!scopeFilters) {
            return next;
        }
        return {
            ...next,
            unitId: scopeFilters.unitId,
            equipmentCategoryId: scopeFilters.equipmentCategoryId,
        };
    };

    const [filters, setFilters] = useState<ReportFilterValues>(() => {
        let base = { ...EMPTY_FILTERS, ...initialFilters };
        // Restore from sessionStorage if available
        if (storageKey) {
            try {
                const saved = sessionStorage.getItem(storageKey);
                if (saved) base = { ...base, ...(JSON.parse(saved) as ReportFilterValues) };
            } catch { /* ignore parse errors */ }
        }
        return mergeScope(base);
    });

    // Re-apply locked unit scope when the parent resolves equipment context.
    useEffect(() => {
        if (!scopeFilters) {
            return;
        }
        setFilters((prev) => mergeScope(prev));
    }, [scopeFilters?.unitId, scopeFilters?.equipmentCategoryId]);

    // On mount, notify parent with the restored/initial filters so the fetch uses them
    const isFirstRender = useRef(true);

    // Notify parent and persist to sessionStorage whenever filters change
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            // Always notify parent on mount so it gets the restored filters
            onFilterChange(filters);
            return;
        }
        onFilterChange(filters);
        if (storageKey) {
            try { sessionStorage.setItem(storageKey, JSON.stringify(filters)); } catch { /* quota */ }
        }
    }, [filters]); // intentionally omit onFilterChange/storageKey — we only react to filter value changes

    // --- Cascading derived lists ---
    // Region → Districts in that region
    const filteredDistricts = useMemo(() => {
        if (!filters.regionId) return companyDistricts;
        return companyDistricts.filter((d) => d.regionalDivisionId === filters.regionId);
    }, [companyDistricts, filters.regionId]);

    // Region + District → Locations
    const filteredLocations = useMemo(() => {
        let locs = companyLocations;
        if (filters.districtId) {
            // `districtId` is not a field on the Location model; preserve the existing
            // runtime lookup (undefined at runtime) without touching the shared type.
            locs = locs.filter((loc) => (loc as { districtId?: string }).districtId === filters.districtId);
        } else if (filters.regionId) {
            locs = locs.filter((loc) => loc.regionalDivisionId === filters.regionId);
        }
        return locs;
    }, [companyLocations, filters.regionId, filters.districtId]);

    // Region/Location → Units at those locations
    const locationScopedUnits = useMemo(() => {
        let units = companyUnits;
        if (filters.locationId) {
            units = units.filter((u) => u.locationId === filters.locationId);
        } else if (filters.regionId) {
            const regionLocIds = new Set(
                companyLocations
                    .filter((loc) => loc.regionalDivisionId === filters.regionId)
                    .map((loc) => loc.id)
            );
            units = units.filter((u) => u.locationId && regionLocIds.has(u.locationId));
        }
        return units;
    }, [companyUnits, companyLocations, filters.regionId, filters.locationId]);

    // Categories narrowed to only those with units in the current region/location scope
    const filteredCategories = useMemo(() => {
        if (!filters.regionId && !filters.locationId) return companyEquipmentCategories;
        const catIdsWithUnits = new Set(locationScopedUnits.map((u) => u.categoryId).filter(Boolean));
        return companyEquipmentCategories.filter((cat) => catIdsWithUnits.has(cat.id));
    }, [companyEquipmentCategories, locationScopedUnits, filters.regionId, filters.locationId]);

    // Units narrowed by category (on top of region/location scope)
    const filteredUnits = useMemo(() => {
        if (!filters.equipmentCategoryId) return locationScopedUnits;
        return locationScopedUnits.filter((u) => u.categoryId === filters.equipmentCategoryId);
    }, [locationScopedUnits, filters.equipmentCategoryId]);

    const update = (patch: Partial<ReportFilterValues>) => {
        setFilters((prev) => mergeScope({ ...prev, ...patch }));
    };

    const handleClear = () => {
        setFilters(mergeScope({ ...EMPTY_FILTERS }));
        if (storageKey) {
            try { sessionStorage.removeItem(storageKey); } catch { /* ignore */ }
        }
    };

    const removeFilter = (key: keyof ReportFilterValues) => {
        if (scopeFilters && (key === 'unitId' || key === 'equipmentCategoryId')) {
            return;
        }
        const patch: Partial<ReportFilterValues> = { [key]: '' };
        // Reset dependent downstream fields
        if (key === 'regionId') { patch.districtId = ''; patch.locationId = ''; patch.equipmentCategoryId = ''; patch.unitId = ''; }
        if (key === 'districtId') { patch.locationId = ''; patch.equipmentCategoryId = ''; patch.unitId = ''; }
        if (key === 'locationId') { patch.equipmentCategoryId = ''; patch.unitId = ''; }
        if (key === 'equipmentCategoryId') patch.unitId = '';
        setFilters((prev) => mergeScope({ ...prev, ...patch }));
    };

    const hasActiveFilters = Object.values(filters).some(Boolean);

    // Build active filter labels for the chip row (matches visual order)
    const activeChips: { key: keyof ReportFilterValues; label: string }[] = [];
    if (filters.regionId) {
        const name = companyRegions.find(r => r.id === filters.regionId)?.name || filters.regionId;
        activeChips.push({ key: 'regionId', label: `Region: ${name}` });
    }
    if (filters.districtId) {
        const name = companyDistricts.find(d => d.id === filters.districtId)?.name || filters.districtId;
        activeChips.push({ key: 'districtId', label: `District: ${name}` });
    }
    if (filters.locationId) {
        const name = companyLocations.find(l => l.id === filters.locationId)?.name || filters.locationId;
        activeChips.push({ key: 'locationId', label: `Location: ${name}` });
    }
    if (filters.equipmentCategoryId) {
        const name = companyEquipmentCategories.find(c => c.id === filters.equipmentCategoryId)?.name || filters.equipmentCategoryId;
        activeChips.push({ key: 'equipmentCategoryId', label: `Category: ${name}` });
    }
    if (filters.unitId) {
        const name = companyUnits.find(u => u.id === filters.unitId)?.name || filters.unitId;
        activeChips.push({ key: 'unitId', label: `Unit: ${name}` });
    }
    if (filters.dateStart) {
        activeChips.push({ key: 'dateStart', label: `From: ${formatStoredDateForDisplay(filters.dateStart)}` });
    }
    if (filters.dateEnd) {
        activeChips.push({ key: 'dateEnd', label: `To: ${formatStoredDateForDisplay(filters.dateEnd)}` });
    }

    return (
        <div className="report-filter-bar">
            <div className="report-filter-row">
                {/* Region → Location → Category → Unit (cascading order) */}
                <div className="report-filter-group">
                    <label htmlFor="report-filter-region">Region</label>
                    <Select
                        inputId="report-filter-region"
                        ariaLabel="Filter by region"
                        value={filters.regionId}
                        onChange={(regionId) =>
                            update({
                                regionId,
                                districtId: '',
                                locationId: '',
                                ...(scopeFilters ? {} : { equipmentCategoryId: '', unitId: '' }),
                            })
                        }
                        options={[
                            { value: '', label: 'All Regions' },
                            ...companyRegions.map((region) => ({ value: region.id, label: region.name })),
                        ]}
                    />
                </div>

                <div className="report-filter-group">
                    <label htmlFor="report-filter-district">District</label>
                    <Select
                        inputId="report-filter-district"
                        ariaLabel="Filter by district"
                        value={filters.districtId}
                        onChange={(districtId) =>
                            update({
                                districtId,
                                locationId: '',
                                ...(scopeFilters ? {} : { equipmentCategoryId: '', unitId: '' }),
                            })
                        }
                        options={[
                            { value: '', label: 'All Districts' },
                            ...filteredDistricts.map((district) => ({ value: district.id, label: district.name })),
                        ]}
                    />
                </div>

                <div className="report-filter-group">
                    <label htmlFor="report-filter-location">Location</label>
                    <Select
                        inputId="report-filter-location"
                        ariaLabel="Filter by location"
                        value={filters.locationId}
                        onChange={(locationId) =>
                            update({
                                locationId,
                                ...(scopeFilters ? {} : { equipmentCategoryId: '', unitId: '' }),
                            })
                        }
                        options={[
                            { value: '', label: 'All Locations' },
                            ...filteredLocations.map((loc) => ({ value: loc.id, label: loc.name })),
                        ]}
                    />
                </div>

                {!scopeFilters && (
                    <div className="report-filter-group">
                        <label htmlFor="report-filter-category">Equipment Category</label>
                        <Select
                            inputId="report-filter-category"
                            ariaLabel="Filter by equipment category"
                            value={filters.equipmentCategoryId}
                            onChange={(equipmentCategoryId) => update({ equipmentCategoryId, unitId: '' })}
                            options={[
                                { value: '', label: 'All Categories' },
                                ...filteredCategories.map((cat) => ({ value: cat.id, label: cat.name || '' })),
                            ]}
                        />
                    </div>
                )}

                {!scopeFilters && (
                    <div className="report-filter-group">
                        <label htmlFor="report-filter-unit">Unit</label>
                        <Select
                            inputId="report-filter-unit"
                            ariaLabel="Filter by unit"
                            value={filters.unitId}
                            onChange={(unitId) => update({ unitId })}
                            options={[
                                { value: '', label: 'All Units' },
                                ...filteredUnits.map((unit) => ({ value: unit.id, label: unit.name || '' })),
                            ]}
                        />
                    </div>
                )}

                <div className="report-filter-group report-filter-group-date-range">
                    <span className="report-filter-group-label" id="report-filter-date-range-label">
                        Date Range
                    </span>
                    <div className="report-filter-date-range" aria-labelledby="report-filter-date-range-label">
                        <DateField
                            className="report-filter-date-field"
                            inputId="report-filter-date-start"
                            value={filters.dateStart}
                            onChange={(dateStart) => update({ dateStart })}
                            placeholder="Start date"
                            ariaLabel="Filter start date"
                        />
                        <span className="report-filter-date-sep">to</span>
                        <DateField
                            className="report-filter-date-field"
                            inputId="report-filter-date-end"
                            value={filters.dateEnd}
                            onChange={(dateEnd) => update({ dateEnd })}
                            placeholder="End date"
                            ariaLabel="Filter end date"
                        />
                    </div>
                </div>

                {hasActiveFilters && (
                    <div className="report-filter-actions">
                        <button className="report-filter-btn" onClick={handleClear}>
                            Clear All
                        </button>
                    </div>
                )}
            </div>

            {/* Active filter chips */}
            {activeChips.length > 0 && (
                <div className="report-filter-chips">
                    {activeChips.map(chip => (
                        <span key={chip.key} className="report-filter-chip">
                            {chip.label}
                            <button
                                onClick={() => removeFilter(chip.key)}
                                aria-label={`Remove ${chip.label} filter`}
                            >×</button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};
